/**
 * ChatPanel — the shared chat UX, used by both /ai (full page) and the
 * global FloatingChat side-sheet. The host owns:
 *   - the active conversation_id (passed in via props)
 *   - any "new chat" callback (so the host can reset the URL or sheet state)
 *
 * Streams via /api/ai/chat (SSE). Persists turns server-side.
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  PaperPlaneTilt,
  Wrench,
  Sparkle,
  CheckCircle,
  XCircle,
  Warning,
  CaretDown,
  CaretRight,
  Stop,
  Paperclip,
  FilePdf,
  Image as ImageIcon,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import {
  fetchConversation,
  streamAiChat,
  uploadAttachments,
  isAcceptedFile,
  bytesToHuman,
  ATTACHMENT_LIMITS,
} from "@/lib/aiChat";
import { useAuth } from "@/lib/auth";
import MarkdownLite from "@/components/MarkdownLite";
import { Button } from "@/components/ui/button";

const SUGGESTIONS = {
  OWNER: [
    "How are we tracking on margin this month?",
    "Show me my pending invoices and the totals.",
    "Which projects are in the watchlist right now?",
    "Draft a quote from these notes: 100ft 6'h cedar fence with 2 gates in Brampton, $90/ft.",
  ],
  REP: [
    "Show me my open leads sorted by oldest follow-up.",
    "Draft a quote from notes: 80ft PT privacy fence — supply & install — Mississauga.",
    "What quotes have I sent this week?",
    "List invoices my client Aisha Khan has open.",
  ],
  CREW_LEAD: [
    "What tasks are due today?",
    "Show me the active projects I'm leading.",
    "Log a follow-up note on lead 'Priya Menon'.",
  ],
  CREW: [
    "What are my tasks for today?",
    "Show me details for project PRJ-2026-0001.",
    "Log 6 hours on task TASK-XYZ for today.",
  ],
};

function ToolCallChip({ block, expanded, onToggle, result }) {
  const Icon = block.name === "confirm_action" ? Warning : Wrench;
  return (
    <div
      className="border border-border rounded-sm bg-secondary/40 my-1.5"
      data-testid={`tool-call-chip-${block.name}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-secondary"
      >
        {expanded ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
        <Icon size={14} weight="duotone" className="text-[#F58216]" />
        <span className="font-mono text-foreground">{block.name}</span>
        {result?.error ? (
          <span className="ref-badge ml-auto !border-rose-500/40 !text-rose-400">error</span>
        ) : result?.requires_confirmation ? (
          <span className="ref-badge ml-auto !border-amber-500/40 !text-amber-400">needs confirm</span>
        ) : (
          <CheckCircle size={14} weight="fill" className="text-emerald-500 ml-auto" />
        )}
      </button>
      {expanded && (
        <div className="px-3 pb-2 pt-1 border-t border-border space-y-2">
          <div>
            <div className="ref-overline mb-1">Input</div>
            <pre className="text-[11px] font-mono whitespace-pre-wrap break-words bg-card p-2 rounded-sm border border-border max-h-40 overflow-y-auto">
              {JSON.stringify(block.input || {}, null, 2)}
            </pre>
          </div>
          {result !== undefined && (
            <div>
              <div className="ref-overline mb-1">Result</div>
              <pre className="text-[11px] font-mono whitespace-pre-wrap break-words bg-card p-2 rounded-sm border border-border max-h-72 overflow-y-auto">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ConfirmationBanner({ payload, onConfirm, onCancel, busy }) {
  return (
    <div
      className="my-2 border border-amber-500/40 bg-amber-500/5 rounded-sm p-3"
      data-testid="ai-confirmation-banner"
    >
      <div className="flex items-start gap-2">
        <Warning size={18} weight="duotone" className="text-amber-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-amber-400 font-bold mb-1">
            Confirmation needed
          </div>
          <div className="text-sm text-foreground mb-3">{payload.preview}</div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              onClick={onConfirm}
              disabled={busy}
              className="!bg-rose-600 hover:!bg-rose-700 !text-white"
              data-testid="ai-confirm-yes-btn"
            >
              <CheckCircle size={14} weight="bold" /> Yes, do it
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onCancel}
              disabled={busy}
              data-testid="ai-confirm-no-btn"
            >
              <XCircle size={14} weight="bold" /> Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AttachmentTile({ att }) {
  const isImage = att.kind === "image" || (att.content_type || "").startsWith("image/");
  return (
    <div
      className="inline-flex items-center gap-2 px-2 py-1.5 rounded-sm border border-border bg-card text-xs max-w-[260px]"
      data-testid={`ai-msg-attachment-${att.file_id}`}
    >
      {isImage ? (
        <ImageIcon size={14} weight="duotone" className="text-[#F58216] shrink-0" />
      ) : (
        <FilePdf size={14} weight="duotone" className="text-[#F58216] shrink-0" />
      )}
      <span className="truncate font-medium text-foreground">
        {att.original_filename || att.file_id}
      </span>
      <span className="text-muted-foreground/70 shrink-0">
        {bytesToHuman(att.size_bytes)}
        {att.page_count ? ` · ${att.page_count}p` : ""}
      </span>
    </div>
  );
}

function MessageBubble({ msg, role, expanded, onToggleExpand, toolResultsById, onConfirm, onCancel, confirming }) {
  if (role === "user") {
    const atts = msg.attachments || [];
    return (
      <div className="flex flex-col items-end gap-1.5" data-testid="ai-msg-user">
        {atts.length > 0 && (
          <div className="flex flex-wrap gap-1.5 max-w-[80%] justify-end">
            {atts.map((a) => (
              <AttachmentTile key={a.file_id} att={a} />
            ))}
          </div>
        )}
        <div className="max-w-[80%] rounded-sm border border-[rgba(245,130,22,0.4)] bg-[rgba(245,130,22,0.08)] px-3 py-2 text-sm text-foreground">
          <MarkdownLite text={msg.content || ""} />
        </div>
      </div>
    );
  }
  // assistant
  const blocks = msg.content_blocks || [];
  const text = msg.content || "";
  const toolUseBlocks = blocks.filter((b) => b.type === "tool_use");
  const pendingConfirmations = msg.confirmations_requested || [];

  return (
    <div className="flex gap-2.5" data-testid="ai-msg-assistant">
      <div className="h-8 w-8 shrink-0 rounded-sm bg-[#F58216] grid place-items-center">
        <Sparkle size={16} weight="fill" className="text-black" />
      </div>
      <div className="flex-1 min-w-0">
        {toolUseBlocks.map((b) => (
          <ToolCallChip
            key={b.id || b.name}
            block={b}
            expanded={!!expanded[b.id]}
            onToggle={() => onToggleExpand(b.id)}
            result={toolResultsById[b.id]}
          />
        ))}
        {text && (
          <div className="rounded-sm border border-border bg-card px-3 py-2.5">
            <MarkdownLite text={text} />
          </div>
        )}
        {pendingConfirmations.map((c) => (
          <ConfirmationBanner
            key={c.token}
            payload={c}
            onConfirm={() => onConfirm(c)}
            onCancel={() => onCancel(c)}
            busy={confirming}
          />
        ))}
        {msg.truncated && (
          // PR-Cg bugfix — inline amber chip rendered under the
          // assistant bubble when the model hit ``max_tokens``.
          // Pre-fix the chat just looked finished even though
          // Sonnet had been chopped mid-tool-call (e.g. partial
          // ``create_quote`` JSON), which silently dropped the
          // action. The chip + the toast above tell the operator
          // to retry with a smaller request.
          <div
            className="mt-2 rounded-sm border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-[11px] text-amber-700 dark:text-amber-400 flex items-start gap-1.5"
            data-testid="ai-message-truncated"
          >
            <span aria-hidden>⚠</span>
            <span>
              Response cut off — try a smaller request or break this into steps.
            </span>
          </div>
        )}
        {msg.cost_usd != null && (
          <div className="ref-overline mt-1 text-muted-foreground">
            ${Number(msg.cost_usd).toFixed(4)} · {msg.tokens_input || 0} in · {msg.tokens_output || 0} out
          </div>
        )}
      </div>
    </div>
  );
}

export default function ChatPanel({
  conversationId,
  onConversationCreated,
  onTurnDone,
  className = "",
  testIdPrefix = "ai",
  compact = false,
}) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]); // chronological list of UI messages
  const [streaming, setStreaming] = useState(false);
  const [input, setInput] = useState("");
  const [expanded, setExpanded] = useState({});
  const [confirming, setConfirming] = useState(false);
  // Pending attachments (uploaded successfully, awaiting send).
  // Each row: {file_id, content_type, kind, size_bytes, original_filename, page_count}
  const [pendingAtts, setPendingAtts] = useState([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const fileInputRef = useRef(null);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  // PR-Cg-Phase7A — Sonnet 5 effort tier. localStorage-persisted so
  // the operator's preference (Medium / High / Max) survives reloads.
  // Default: ``"high"`` matches the server default. Passed through
  // to ``streamAiChat({effort})`` on every turn.
  const [effort, setEffort] = useState(() => {
    try {
      const raw = localStorage.getItem("ai_chat_effort");
      if (raw === "medium" || raw === "high" || raw === "max") return raw;
    } catch { /* localStorage disabled — in-session default only */ }
    return "high";
  });
  const setEffortPersisted = useCallback((v) => {
    setEffort(v);
    try { localStorage.setItem("ai_chat_effort", v); } catch { /* noop */ }
  }, []);

  const role = user?.role || "OWNER";
  const suggestions = SUGGESTIONS[role] || SUGGESTIONS.OWNER;

  // Single-shot flag: when the SSE meta event sets `conversationId` mid-stream
  // we don't want the load-history effect to refetch and overwrite the
  // bubbles we're streaming (BUG #1 from iter-15: floating chat's first
  // message vanished until the user closed and reopened the sheet). Setting
  // this flag right before calling onConversationCreated tells the next
  // effect run to no-op exactly once.
  const skipNextLoadRef = useRef(false);

  // Load history when conversationId changes (and it's a real id, not null)
  useEffect(() => {
    let cancelled = false;
    if (skipNextLoadRef.current) {
      skipNextLoadRef.current = false;
      return;
    }
    if (!conversationId) {
      setMessages([]);
      setExpanded({});
      return;
    }
    (async () => {
      try {
        const conv = await fetchConversation(conversationId);
        if (cancelled) return;
        // Convert backend messages → UI messages.
        // Build the array in a local variable, then setMessages with the
        // FINAL fully-built array so React StrictMode's double-invocation
        // sees no in-place mutation.
        const ui = [];
        let lastAssistantIndex = -1;
        for (const m of conv.messages || []) {
          if (m.role === "user") {
            ui.push({
              ui_id: m.id,
              role: "user",
              content: m.content || "",
              attachments: m.attachments || [],
              created_at: m.created_at,
            });
            lastAssistantIndex = -1;
          } else if (m.role === "assistant") {
            ui.push({
              ui_id: m.id,
              role: "assistant",
              content: m.content || "",
              content_blocks: m.content_blocks || [],
              tokens_input: m.tokens_input,
              tokens_output: m.tokens_output,
              cost_usd: m.cost_usd,
              // PR-Cg bugfix — re-derive ``truncated`` from the
              // persisted ``stop_reason`` so the amber chip
              // survives a page refresh. Without this the warning
              // disappears on reload even though the underlying
              // message is still truncated.
              truncated: m.stop_reason === "max_tokens",
              stop_reason: m.stop_reason,
              tool_results_by_id: {},
              confirmations_requested: [],
              created_at: m.created_at,
            });
            lastAssistantIndex = ui.length - 1;
          } else if (m.role === "tool_results") {
            // Attach to last assistant — but immutably: we replace the entry
            // in `ui` with a copy that has the merged tool_results_by_id.
            if (lastAssistantIndex < 0) continue;
            const prev = ui[lastAssistantIndex];
            const merged = { ...(prev.tool_results_by_id || {}) };
            for (const block of m.content_blocks || []) {
              if (block.type === "tool_result" && block.tool_use_id) {
                let parsed;
                try {
                  parsed =
                    typeof block.content === "string"
                      ? JSON.parse(block.content)
                      : block.content;
                } catch (e) {
                  parsed = { raw: block.content };
                }
                merged[block.tool_use_id] = parsed;
              }
            }
            ui[lastAssistantIndex] = { ...prev, tool_results_by_id: merged };
          }
        }
        if (!cancelled) setMessages(ui);
      } catch (e) {
        // E6 chatbot 404 fix — if the cached conversationId points at a
        // conversation that no longer exists on the server, hand the
        // null back to the parent so it clears its state (and
        // localStorage). The chat resets to the fresh empty-state
        // suggestions, and the next send mints a new conversation
        // instead of POSTing the dead id.
        if (e?.status === 404) {
          if (!cancelled) {
            setMessages([]);
            setExpanded({});
            onConversationCreated?.(null);
          }
          return;
        }
        toast.error("Couldn't load conversation history");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  // Auto-scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, streaming]);

  const send = useCallback(
    async (raw) => {
      const text = (raw || "").trim();
      if (!text || streaming) return;
      if (uploadingCount > 0) {
        toast.error("Wait for files to finish uploading");
        return;
      }
      setInput("");

      const sentAtts = pendingAtts;
      // Reset pending attachments so the chips clear immediately on send.
      setPendingAtts([]);

      const userMsg = {
        ui_id: `local-${Date.now()}`,
        role: "user",
        content: text,
        attachments: sentAtts,
      };
      const assistantMsg = {
        ui_id: `local-asst-${Date.now()}`,
        role: "assistant",
        content: "",
        content_blocks: [],
        tool_results_by_id: {},
        confirmations_requested: [],
        streaming: true,
      };
      setMessages((m) => [...m, userMsg, assistantMsg]);
      setStreaming(true);

      const abort = new AbortController();
      abortRef.current = abort;
      let activeConvId = conversationId;

      try {
        await streamAiChat({
          message: text,
          conversationId: conversationId || null,
          attachments: sentAtts.length ? sentAtts.map((a) => a.file_id) : null,
          // PR-Cg-Phase7A — effort tier for this turn. localStorage-
          // persisted at the setter; captured here into the request.
          effort,
          signal: abort.signal,
          onEvent: ({ event, data }) => {
            if (event === "meta") {
              if (data?.conversation_id && !activeConvId) {
                activeConvId = data.conversation_id;
                // Tell the next conversationId-effect to skip the reload —
                // we already have the bubbles in local state and a fresh
                // GET /conversations/{id} would race-overwrite them.
                skipNextLoadRef.current = true;
                onConversationCreated?.(data.conversation_id);
              }
            } else if (event === "token") {
              // PURE updater (BUG iter-16): React StrictMode runs every
              // setState updater twice in dev. Mutating `last.content` here
              // would re-apply the delta on the second pass and produce
              // doubled output ("HelloHello"). Always return a fresh object.
              setMessages((prev) => {
                if (!prev.length) return prev;
                const lastIdx = prev.length - 1;
                const last = prev[lastIdx];
                if (last.role !== "assistant") return prev;
                const newLast = {
                  ...last,
                  content: (last.content || "") + (data?.text || ""),
                };
                return [...prev.slice(0, lastIdx), newLast];
              });
            } else if (event === "tool_use") {
              setMessages((prev) => {
                if (!prev.length) return prev;
                const lastIdx = prev.length - 1;
                const last = prev[lastIdx];
                if (last.role !== "assistant") return prev;
                const block = {
                  type: "tool_use",
                  id: data?.id,
                  name: data?.name,
                  input: data?.input || {},
                };
                // De-dupe by id — defends against any future scenario where the
                // same tool_use event arrives twice (rare but cheap to guard).
                const existing = last.content_blocks || [];
                if (block.id && existing.some((b) => b.id === block.id)) {
                  return prev;
                }
                const newLast = {
                  ...last,
                  content_blocks: [...existing, block],
                };
                return [...prev.slice(0, lastIdx), newLast];
              });
            } else if (event === "tool_result") {
              setMessages((prev) => {
                if (!prev.length) return prev;
                const lastIdx = prev.length - 1;
                const last = prev[lastIdx];
                if (last.role !== "assistant") return prev;
                const newLast = {
                  ...last,
                  tool_results_by_id: {
                    ...(last.tool_results_by_id || {}),
                    [data?.tool_use_id]: data?.result,
                  },
                };
                return [...prev.slice(0, lastIdx), newLast];
              });
            } else if (event === "confirmation_required") {
              setMessages((prev) => {
                if (!prev.length) return prev;
                const lastIdx = prev.length - 1;
                const last = prev[lastIdx];
                if (last.role !== "assistant") return prev;
                const existing = last.confirmations_requested || [];
                // De-dupe by token.
                if (data?.token && existing.some((c) => c.token === data.token)) {
                  return prev;
                }
                const newLast = {
                  ...last,
                  confirmations_requested: [
                    ...existing,
                    {
                      token: data?.token,
                      preview: data?.preview,
                      tool: data?.tool,
                    },
                  ],
                };
                return [...prev.slice(0, lastIdx), newLast];
              });
            } else if (event === "done") {
              setMessages((prev) => {
                if (!prev.length) return prev;
                const lastIdx = prev.length - 1;
                const last = prev[lastIdx];
                if (last.role !== "assistant") return prev;
                const newLast = {
                  ...last,
                  streaming: false,
                  cost_usd: data?.cost_usd,
                  tokens_input: data?.usage?.input_tokens,
                  tokens_output: data?.usage?.output_tokens,
                  // PR-Cg bugfix — surface the truncation flag so the
                  // bubble can render the amber "response cut off"
                  // chip. Pre-fix the FE ignored ``stop_reason``
                  // entirely; chats just looked finished even though
                  // Sonnet had been chopped mid-tool-call.
                  truncated: data?.truncated === true,
                  stop_reason: data?.stop_reason,
                };
                return [...prev.slice(0, lastIdx), newLast];
              });
              if (data?.truncated === true) {
                toast.error(
                  "AI response was cut off (response too long). " +
                  "Try a smaller request or break this into steps.",
                  { duration: 8000 },
                );
              }
              onTurnDone?.(data);
            } else if (event === "error") {
              setMessages((prev) => {
                if (!prev.length) return prev;
                const lastIdx = prev.length - 1;
                const last = prev[lastIdx];
                if (last.role !== "assistant") return prev;
                const newLast = {
                  ...last,
                  streaming: false,
                  content:
                    (last.content || "") +
                    `\n\n_Error: ${data?.error || "unknown"}${
                      data?.detail ? ` — ${data.detail}` : ""
                    }_`,
                };
                return [...prev.slice(0, lastIdx), newLast];
              });
              toast.error(`AI: ${data?.error || "unknown error"}`);
            }
          },
        });
      } catch (e) {
        if (e?.name !== "AbortError") {
          toast.error("Chat failed: " + (e.message || e));
        }
        setMessages((prev) => {
          if (!prev.length) return prev;
          const lastIdx = prev.length - 1;
          const last = prev[lastIdx];
          if (last.role !== "assistant" || !last.streaming) return prev;
          const newLast = { ...last, streaming: false };
          return [...prev.slice(0, lastIdx), newLast];
        });
      } finally {
        setStreaming(false);
        abortRef.current = null;
      }
    },
    [streaming, uploadingCount, pendingAtts, conversationId, onConversationCreated, onTurnDone]
  );

  const stop = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setStreaming(false);
  }, []);

  const onConfirm = useCallback(
    async (c) => {
      setConfirming(true);
      try {
        await send(`Yes, please confirm. Use confirm_action with confirmation_token="${c.token}" and confirmed=true.`);
      } finally {
        setConfirming(false);
      }
    },
    [send]
  );

  const onCancel = useCallback(
    async (c) => {
      setConfirming(true);
      try {
        await send(`No, cancel that. Use confirm_action with confirmation_token="${c.token}" and confirmed=false.`);
      } finally {
        setConfirming(false);
      }
    },
    [send]
  );

  const toggleExpand = useCallback((id) => {
    if (!id) return;
    setExpanded((e) => ({ ...e, [id]: !e[id] }));
  }, []);

  // ─── Attachments — file pick / upload / remove ─────────────────────-
  const onPickFiles = useCallback(
    async (e) => {
      const list = Array.from(e.target.files || []);
      // Allow re-picking the same files (clear value)
      e.target.value = "";
      if (!list.length) return;

      // Validate type + size + count BEFORE upload
      const room = ATTACHMENT_LIMITS.MAX_FILES - pendingAtts.length;
      if (list.length > room) {
        toast.error(
          `Max ${ATTACHMENT_LIMITS.MAX_FILES} files per message — ${pendingAtts.length} already attached`
        );
        return;
      }
      for (const f of list) {
        if (!isAcceptedFile(f)) {
          toast.error(`${f.name}: unsupported file type (${f.type || "unknown"})`);
          return;
        }
        if (f.size > ATTACHMENT_LIMITS.MAX_BYTES) {
          toast.error(`${f.name}: too large (${bytesToHuman(f.size)} > 10 MB)`);
          return;
        }
      }

      setUploadingCount((n) => n + list.length);
      try {
        const rows = await uploadAttachments(list);
        setPendingAtts((prev) => [...prev, ...rows]);
      } catch (err) {
        toast.error("Upload failed: " + (err?.message || err));
      } finally {
        setUploadingCount((n) => Math.max(0, n - list.length));
      }
    },
    [pendingAtts.length]
  );

  const removeAttachment = useCallback((file_id) => {
    setPendingAtts((prev) => prev.filter((a) => a.file_id !== file_id));
  }, []);

  // Enter sends, Shift+Enter inserts a newline (per iter-17 spec).
  // Cmd/Ctrl+Enter still works for muscle memory.
  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      send(input);
    }
  };

  // Auto-grow the textarea between min and max via scrollHeight measurement.
  // Cheaper than `field-sizing: content` (still spotty browser support) and
  // works in StrictMode without infinite re-renders since we just write style.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const next = Math.min(el.scrollHeight, 256); // 16rem cap
    el.style.height = next + "px";
  }, [input]);

  return (
    <div className={`flex flex-col h-full min-h-0 ${className}`} data-testid={`${testIdPrefix}-chat-panel`}>
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4"
        data-testid={`${testIdPrefix}-chat-scroll`}
      >
        {messages.length === 0 ? (
          <EmptyState
            user={user}
            suggestions={suggestions}
            onPick={(s) => send(s)}
            compact={compact}
            testIdPrefix={testIdPrefix}
          />
        ) : (
          <>
            {messages.map((m) => {
              const tools_by_id = m.tool_results_by_id || {};
              return (
                <MessageBubble
                  key={m.ui_id}
                  msg={m}
                  role={m.role}
                  expanded={expanded}
                  onToggleExpand={toggleExpand}
                  toolResultsById={tools_by_id}
                  onConfirm={onConfirm}
                  onCancel={onCancel}
                  confirming={confirming}
                />
              );
            })}
            {streaming && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="ai-streaming-indicator">
                <motion.span
                  className="h-1.5 w-1.5 rounded-full bg-[#F58216]"
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 1.2, repeat: Infinity }}
                />
                Working…
              </div>
            )}
          </>
        )}
      </div>

      <div className="border-t border-border p-3 bg-card">
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,application/pdf"
          multiple
          onChange={onPickFiles}
          className="hidden"
          data-testid={`${testIdPrefix}-chat-file-input`}
        />
        {/* Attachment chips */}
        {pendingAtts.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`${testIdPrefix}-chat-attachments-row`}>
            {pendingAtts.map((a) => (
              <div
                key={a.file_id}
                className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-sm border border-[#F58216]/40 bg-[rgba(245,130,22,0.06)] text-xs"
                data-testid={`${testIdPrefix}-chat-attachment-chip-${a.file_id}`}
              >
                {a.kind === "image" ? (
                  <ImageIcon size={12} weight="duotone" className="text-[#F58216]" />
                ) : (
                  <FilePdf size={12} weight="duotone" className="text-[#F58216]" />
                )}
                <span className="font-medium text-foreground truncate max-w-[150px]">
                  {a.original_filename}
                </span>
                <span className="text-muted-foreground/70">
                  {bytesToHuman(a.size_bytes)}
                  {a.page_count ? ` · ${a.page_count}p` : ""}
                </span>
                <button
                  type="button"
                  onClick={() => removeAttachment(a.file_id)}
                  className="p-0.5 rounded-sm hover:bg-secondary text-muted-foreground hover:text-rose-500"
                  aria-label="Remove attachment"
                  data-testid={`${testIdPrefix}-chat-attachment-remove-${a.file_id}`}
                >
                  <X size={10} weight="bold" />
                </button>
              </div>
            ))}
            {uploadingCount > 0 && (
              <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-border bg-card text-xs text-muted-foreground">
                <motion.span
                  className="h-1.5 w-1.5 rounded-full bg-[#F58216]"
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 1, repeat: Infinity }}
                />
                Uploading {uploadingCount}…
              </div>
            )}
          </div>
        )}
        <div className="relative flex items-end gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={streaming || pendingAtts.length >= ATTACHMENT_LIMITS.MAX_FILES}
            className={[
              "h-10 w-10 shrink-0 grid place-items-center rounded-sm border border-border",
              "text-muted-foreground hover:text-[#F58216] hover:border-[#F58216] transition-colors",
              "disabled:opacity-40 disabled:cursor-not-allowed",
            ].join(" ")}
            aria-label="Attach files"
            data-testid={`${testIdPrefix}-chat-attach-btn`}
          >
            <Paperclip size={16} weight="duotone" />
          </button>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Ask anything, paste meeting notes, or attach a file (PDF/image)…"
            rows={compact ? 3 : 3}
            disabled={streaming}
            className="flex-1 resize-none rounded-sm border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#F58216] focus:ring-0 disabled:opacity-60 leading-relaxed"
            style={{ maxHeight: "16rem", minHeight: "4.5rem" }}
            data-testid={`${testIdPrefix}-chat-input`}
          />
          {streaming ? (
            <Button
              type="button"
              onClick={stop}
              variant="outline"
              className="h-10 w-10 p-0"
              data-testid={`${testIdPrefix}-chat-stop-btn`}
            >
              <Stop size={16} weight="fill" className="text-rose-500" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => send(input)}
              disabled={!input.trim() || uploadingCount > 0}
              className="h-10 w-10 p-0 !bg-[#F58216] hover:!bg-[#F58216]/90 !text-black"
              data-testid={`${testIdPrefix}-chat-send-btn`}
            >
              <PaperPlaneTilt size={16} weight="fill" />
            </Button>
          )}
        </div>
        {/* PR-Cg-Phase7A — Sonnet 5 effort tier segmented control.
            Sits below the composer so it's always visible without
            competing with the input focus. Selection persists to
            localStorage (``ai_chat_effort``) and is sent as the
            ``effort`` field on every ``POST /api/ai/chat``. */}
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          <div
            className="inline-flex items-center rounded-sm border border-border overflow-hidden"
            role="radiogroup"
            aria-label="AI thinking effort"
            data-testid={`${testIdPrefix}-ai-chat-effort-toggle`}
          >
            {["medium", "high", "max"].map((val) => (
              <button
                key={val}
                type="button"
                role="radio"
                aria-checked={effort === val}
                disabled={streaming}
                onClick={() => setEffortPersisted(val)}
                className={[
                  "px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold transition-colors",
                  effort === val
                    ? "bg-[#F58216] text-black"
                    : "text-muted-foreground hover:text-foreground",
                  "disabled:opacity-40 disabled:cursor-not-allowed",
                ].join(" ")}
                data-testid={`${testIdPrefix}-ai-chat-effort-${val}`}
              >
                {val === "medium" ? "Medium" : val === "high" ? "High" : "Max"}
              </button>
            ))}
          </div>
          <span
            className="text-[10px] text-muted-foreground/80 cursor-help"
            title="Higher effort = deeper thinking, higher latency, higher cost. Max includes extended reasoning."
            data-testid={`${testIdPrefix}-ai-chat-effort-tooltip`}
          >
            ⓘ effort
          </span>
        </div>
        <div className="ref-overline mt-1.5 text-muted-foreground/70">
          Enter sends · Shift+Enter newline · ≤ {ATTACHMENT_LIMITS.MAX_FILES} files, ≤ 10 MB each
        </div>
      </div>
    </div>
  );
}

function EmptyState({ user, suggestions, onPick, compact, testIdPrefix }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center p-6">
      <div className="h-12 w-12 rounded-sm bg-[#F58216] grid place-items-center mb-3">
        <Sparkle size={22} weight="fill" className="text-black" />
      </div>
      <div className="font-display font-bold text-lg">Reformity AI</div>
      <div className="text-xs text-muted-foreground mt-1 mb-5 max-w-sm">
        Ask anything about your CRM. {user?.name ? `${user.name},` : "You're"} signed in as{" "}
        <span className="text-[#F58216] font-semibold">{user?.role}</span> — only tools your role allows are available.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl w-full">
        {suggestions.slice(0, compact ? 2 : 4).map((s, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onPick(s)}
            className="text-left p-3 rounded-sm border border-border bg-card hover:border-[#F58216] hover:bg-[rgba(245,130,22,0.04)] transition-colors text-sm text-foreground"
            data-testid={`${testIdPrefix}-suggestion-${i}`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}
